import { useEffect, useState } from 'react';

/**
 * Custom hook that tracks changes to a video element ref and triggers re-renders
 * when the actual video element changes. This is useful for components that need
 * to react to video element changes that don't normally trigger React re-renders.
 */
export function useVideoElementTracker(videoRef: React.RefObject<HTMLVideoElement | null>) {
    const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(null);
    const [updateKey, setUpdateKey] = useState(0);

    useEffect(() => {
        const currentElement = videoRef.current;

        if (currentElement !== videoElement) {
            console.log('useVideoElementTracker: Video element changed', {
                old: !!videoElement,
                new: !!currentElement,
            });
            setVideoElement(currentElement);
            setUpdateKey((prev) => prev + 1);
        }

        // Set up an interval to check for changes (as a fallback)
        const interval = setInterval(() => {
            const current = videoRef.current;
            if (current !== videoElement) {
                console.log('useVideoElementTracker: Video element changed (interval check)');
                setVideoElement(current);
                setUpdateKey((prev) => prev + 1);
            }
        }, 100);

        return () => {
            clearInterval(interval);
        };
    }, [videoRef, videoElement]);

    return {
        videoElement,
        updateKey,
        hasVideoElement: !!videoElement,
    };
}
